import { faEnvelope, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { displayPopin } from "../../Hook/popins";
import useUser from "../../Hook/user";
import Button from "../Input/Button";

const DISMISS_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const STORAGE_KEY = "emailConfirmBannerDismissed";

function EmailConfirmBanner() {
  const user = useUser();
  const display = displayPopin();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    // Check if banner was dismissed within the last 5 minutes
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      if (now - dismissedTime < DISMISS_DURATION) {
        setDismissed(true);
      } else {
        // Clear expired dismissal
        localStorage.removeItem(STORAGE_KEY);
        setDismissed(false);
      }
    } else {
      setDismissed(false);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setDismissed(true);
  };

  const handleVerifyClick = () => {
    display("emailconfirm");
  };

  // Don't show if user is not logged in
  if (!user || !user.informations) return null;

  // Don't show if user is a guest
  if (user.informations.type === "guest") return null;

  // Don't show if email is already confirmed
  if (user.informations.is_email_confirmed) return null;

  // Don't show if dismissed
  if (dismissed) return null;

  return (
    <div className="w-full flex justify-center px-4 sm:px-6 lg:px-8 py-4 mt-2">
      <div className="max-w-4xl w-full">
        <div className="bg-white border-2 border-primary rounded-2xl shadow-lg relative">
          {/* Close button - top right on mobile and desktop */}
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100 z-10"
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>

          <div className="px-6 sm:px-8 py-6 pr-12">
            {/* Mobile: stacked layout, Desktop: horizontal layout */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Icon and text */}
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center border-2 border-primary">
                    <FontAwesomeIcon icon={faEnvelope} className="text-primary text-2xl" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-semibold text-gray-900">
                    <FormattedMessage id="email_confirm_banner__title" defaultMessage="Verify your email address" />
                  </p>
                  <p className="text-base text-gray-600 mt-1">
                    <FormattedMessage
                      id="email_confirm_banner__description"
                      defaultMessage="Please verify your email to access all features and secure your account."
                    />
                  </p>
                </div>
              </div>

              {/* Button - centered on mobile, right-aligned on desktop */}
              <div className="flex justify-center sm:justify-end sm:flex-shrink-0 sm:mr-4">
                <Button
                  color="primary"
                  onClick={handleVerifyClick}
                  className="!px-4 sm:!px-6 !py-2.5 text-sm font-medium shadow-md hover:shadow-lg transition-shadow whitespace-nowrap"
                >
                  <FormattedMessage id="email_confirm_banner__verify_button" defaultMessage="Verify Email" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailConfirmBanner;
